<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto" @click.self="handleBackdropClick">
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>
        <div class="flex min-h-full items-center justify-center p-4">
          <div :class="modalSizeClass" class="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all w-full"
            @click.stop>
            <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ title }}</h3>
              <button @click="handleClose" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              <slot></slot>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'

/**
 * Modal 组件属性
 */
interface Props {
  /** 是否显示 Modal */
  show: boolean
  /** Modal 标题 */
  title: string
  /** Modal 尺寸 */
  size?: 'small' | 'medium' | 'large' | 'xlarge'
  /** 是否允许点击背景关闭 Modal，默认为 false（不允许），防止误操作 */
  closeOnBackdrop?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  closeOnBackdrop: false,
})

const emit = defineEmits<{
  close: []
}>()

const modalSizeClass = computed(() => {
  const sizeMap = {
    small: 'max-w-md',
    medium: 'max-w-2xl',
    large: 'max-w-4xl',
    xlarge: 'max-w-6xl',
  }
  return sizeMap[props.size]
})

/**
 * 处理点击背景事件
 * 只有在 closeOnBackdrop 为 true 时才关闭 Modal
 */
const handleBackdropClick = () => {
  if (props.closeOnBackdrop) {
    emit('close')
  }
}

/**
 * 处理关闭 Modal
 * 点击关闭按钮或取消按钮时调用
 */
const handleClose = () => {
  emit('close')
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
}
</style>
